import React, { useEffect, useRef, useState } from "react";
import { TenantLogo } from "@/components/TenantLogo";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

export default function AvgsAppointmentSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const padRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/appointments/sign/${token}`);
        setInfo(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!signature) { toast.error("Bitte unterschreiben"); return; }
    setSaving(true);
    setSubmitError(null);
    try {
      await api.post(`/appointments/sign/${token}`, { signature });
      setDone(true);
      toast.success("Termin unterschrieben");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setSubmitError(msg);
      toast.error(msg);
      if ((e.response?.data?.detail || "").includes("konnte nicht verarbeitet werden")) {
        padRef.current?.clear();
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-xs text-slate-500">Lädt…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="max-w-md bg-white border border-slate-200 p-8">
        <div className="text-xs font-bold uppercase tracking-overline text-red-600 mb-2">Fehler</div>
        <div className="text-sm text-slate-700">{error}</div>
        <a href="/mein-bereich" className="text-xs text-[#0a788c] underline mt-4 inline-block">Zum Portal einloggen</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-lg bg-white border border-slate-200" data-testid="avgs-sign-page">
        <div className="px-8 py-6 border-b border-slate-100">
          <div className="mb-3"><TenantLogo className="w-40 h-auto" testId="sign-page-logo" /></div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-1">Termin unterschreiben</div>
          <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">Hallo {info.client_name?.split(" ")[0] || info.client_name}.</div>
          <div className="text-xs text-slate-500 mt-1">Dein Coach {info.coach_name} hat den Termin als &bdquo;stattgefunden&ldquo; markiert und wartet auf deine Unterschrift.</div>
        </div>

        <div className="px-8 py-6 space-y-4">
          <table className="text-sm w-full">
            <tbody>
              <tr><td className="py-2 text-slate-500 text-xs">Datum</td><td className="py-2 font-mono">{formatDeShort(info.date)}</td></tr>
              <tr><td className="py-2 text-slate-500 text-xs">Uhrzeit</td><td className="py-2 font-mono">{info.time_from || "?"}–{info.time_to || "?"} Uhr</td></tr>
              <tr><td className="py-2 text-slate-500 text-xs">Umfang</td><td className="py-2 font-mono">{Number(info.planned_ue || 0)} UE · {info.mode === "online" ? "Remote" : "Präsenz"}</td></tr>
              {info.topic && <tr><td className="py-2 text-slate-500 text-xs">Thema</td><td className="py-2">{info.topic}</td></tr>}
              <tr><td className="py-2 text-slate-500 text-xs">Coach</td><td className="py-2">{info.coach_name}</td></tr>
            </tbody>
          </table>

          {info.coach_signature && (
            <div className="border border-slate-100 p-3 bg-slate-50">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">Unterschrift des Coaches</div>
              <img src={info.coach_signature} alt="Coach signature" className="h-14" />
            </div>
          )}

          {done || info.already_signed ? (
            <div className="p-6 bg-emerald-50 border border-emerald-200 text-center" data-testid="avgs-sign-done">
              <div className="text-2xl mb-2">✓</div>
              <div className="font-medium text-emerald-800">Termin bereits unterschrieben</div>
              {info.client_signed_at && (
                <div className="text-xs text-emerald-700 mt-1 font-mono">
                  {new Date(info.client_signed_at).toLocaleString("de-DE")}
                </div>
              )}
              <div className="text-xs text-emerald-700 mt-3">Die UE werden nun ordnungsgemäß vom Bildungsgutschein abgezogen. Vielen Dank!</div>
            </div>
          ) : info.status !== "completed" ? (
            <div className="p-6 bg-amber-50 border border-amber-200 text-center">
              <div className="text-sm text-amber-900">Dieser Termin ist noch nicht vom Coach abgeschlossen. Bitte melde dich, wenn du glaubst, dass hier etwas nicht stimmt.</div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Deine Unterschrift</label>
                <SignaturePad ref={padRef} value={signature} onChange={setSignature} />
                <div className="text-[10px] text-slate-500 mt-2">Mit deiner Unterschrift bestätigst du die Teilnahme am oben genannten Coaching-Termin. Kein Login nötig.</div>
              </div>
              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 text-sm text-red-800" data-testid="avgs-sign-error">
                  <b>Unterschrift konnte nicht gespeichert werden:</b> {submitError}
                  {submitError.includes("konnte nicht verarbeitet werden") && (
                    <div className="mt-1 text-xs text-red-700">Deine Unterschrift wurde zurückgesetzt — bitte zeichne sie unten noch einmal und versuche es erneut.</div>
                  )}
                </div>
              )}
              <button
                onClick={submit}
                disabled={saving || !signature}
                data-testid="avgs-sign-submit"
                className="w-full bg-[#0a788c] text-white py-4 text-sm font-medium hover:bg-[#0a96b4] disabled:opacity-50"
              >
                {saving ? "Speichere…" : "Termin unterschreiben"}
              </button>
              <div className="text-center text-xs text-slate-500">
                Oder <a href="/mein-bereich" className="text-[#0a788c] underline">im Portal einloggen</a> und dort unterschreiben.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
